import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

export default function Engagements() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [engs, setEngs] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", organization_id: "", start_date: "", end_date: "", consultant_ids: [], planned_days: 0, notes: "" });

  const load = () => api.get("/engagements").then((r) => setEngs(r.data));
  useEffect(() => {
    load();
    api.get("/organizations").then((r) => setOrgs(r.data));
    if (isAdmin) api.get("/admin/consultants").then((r) => setConsultants(r.data));
  }, []);

  const add = async () => {
    try {
      await api.post("/engagements", form);
      setShowForm(false); load(); toast.success("Engagement created.");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!engs) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="engagements-loading">Loading engagements…</div>;

  return (
    <div className="max-w-6xl space-y-6" data-testid="engagements-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">Contracts</p>
          <h1 className="section-title text-3xl mt-1">Service Engagements</h1>
        </div>
        {isAdmin && <button className="btn-outline" onClick={() => setShowForm(!showForm)} data-testid="add-engagement-button">
          {showForm ? <X size={15} /> : <Plus size={15} />} New engagement</button>}
      </div>

      {showForm && (
        <div className="card p-6 grid sm:grid-cols-2 gap-4 fade-up" data-testid="add-engagement-form">
          <div className="sm:col-span-2"><label className="label">Title</label>
            <input className="input" value={form.title} data-testid="engagement-title-input"
              onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><label className="label">Organization</label>
            <select className="input" value={form.organization_id} data-testid="engagement-org-select"
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}>
              <option value="">Select…</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></div>
          <div><label className="label">Planned service days</label>
            <input className="input" type="number" min="0" value={form.planned_days} data-testid="engagement-days-input"
              onChange={(e) => setForm({ ...form, planned_days: Number(e.target.value) })} /></div>
          <div><label className="label">Start date</label>
            <input className="input" type="date" value={form.start_date} data-testid="engagement-start-input"
              onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
          <div><label className="label">End date</label>
            <input className="input" type="date" value={form.end_date} data-testid="engagement-end-input"
              onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          <div className="sm:col-span-2"><label className="label">Assigned consultants</label>
            <div className="flex flex-wrap gap-2" data-testid="engagement-consultants-select">
              {consultants.map((c) => (
                <button key={c.id} type="button" data-testid={`engagement-consultant-${c.id}`}
                  onClick={() => setForm({ ...form, consultant_ids: form.consultant_ids.includes(c.id) ? form.consultant_ids.filter((x) => x !== c.id) : [...form.consultant_ids, c.id] })}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold border transition-colors ${
                    form.consultant_ids.includes(c.id) ? "bg-terracotta text-white border-terracotta" : "bg-white text-navy border-[#E7E1D4]"}`}>
                  {c.name}</button>
              ))}
            </div></div>
          <div className="sm:col-span-2"><label className="label">Notes</label>
            <textarea className="input" value={form.notes} data-testid="engagement-notes-input"
              onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div><button className="btn-primary" onClick={add}
            disabled={!form.title || !form.organization_id || !form.start_date || !form.end_date}
            data-testid="save-engagement-button">Create engagement</button></div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5 fade-up-1">
        {engs.map((e) => (
          <Link key={e.id} to={`/engagements/${e.id}`} className="card card-hover p-6 block" data-testid={`engagement-card-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-serif font-bold text-lg text-navy leading-snug">{e.title}</h2>
              <span className={e.status === "active" ? "badge-approved" : "badge-neutral"}>{e.status}</span>
            </div>
            <div className="mt-2 text-sm text-[#57534E]">{e.organization_name}</div>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#78716C]">
              <span><b className="text-navy">{e.start_date}</b> → <b className="text-navy">{e.end_date}</b></span>
              <span>Consultants: <b className="text-navy">{e.consultant_names.filter(Boolean).join(", ") || "—"}</b></span>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs font-bold text-[#78716C] mb-1">
                <span>Service days delivered</span><span>{e.days_delivered} / {e.planned_days}</span>
              </div>
              <div className="h-2 rounded-full bg-sand overflow-hidden">
                <div className="h-full bg-terracotta rounded-full transition-all"
                  style={{ width: `${e.planned_days ? Math.min(100, (e.days_delivered / e.planned_days) * 100) : 0}%` }} />
              </div>
            </div>
          </Link>
        ))}
        {engs.length === 0 && <p className="text-sm text-[#78716C]" data-testid="engagements-empty">No engagements in scope.</p>}
      </div>
    </div>
  );
}
